import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserPlus } from 'lucide-react'
import { listCustomers } from '../services/data'
import { PageHeader, Card, DataTable, SearchInput, Pagination, Spinner, Button, FilterBar } from '../components/shared/ui'
import { customerLabel } from '../lib/format'
import { useServerList } from '../lib/list'
import CustomerForm from '../components/CustomerForm'
import type { Customer } from '../types'

const PAGE_SIZE = 25

export default function Customers() {
  const navigate = useNavigate()
  const [showNew, setShowNew] = useState(false)
  const { rows, count, page, search, sort, loading, setPage, setSearch, setFilters, toggleSort, reload } =
    useServerList<Customer>(listCustomers, { pageSize: PAGE_SIZE, defaultSort: { column: 'name', dir: 'asc' } })

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle={`${count.toLocaleString()} total`}
        actions={<Button onClick={() => setShowNew(true)}><UserPlus size={16} /> New Customer</Button>}
      />
      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, email, phone…" />
          <FilterBar
            onChange={setFilters}
            fields={[
              { key: 'city', label: 'City', column: 'city', type: 'text', placeholder: 'City' },
              { key: 'state', label: 'State', column: 'state', type: 'text', placeholder: 'State' },
              { key: 'zip', label: 'Zip', column: 'zip', type: 'text', placeholder: 'Zip' },
            ]}
          />
        </div>
        {loading ? (
          <Spinner label="Loading customers…" />
        ) : (
          <>
            <DataTable
              rows={rows}
              sort={sort}
              onSort={toggleSort}
              onRowClick={c => navigate(`/customers/${c.id}`)}
              columns={[
                { key: 'name', header: 'Name', render: c => <span className="font-medium text-gray-900">{customerLabel(c)}</span>, sortKey: 'name' },
                { key: 'phone', header: 'Phone', render: c => c.phone || c.phone_home || '—', sortKey: 'phone' },
                { key: 'email', header: 'Email', render: c => c.email || '—', sortKey: 'email' },
                { key: 'city', header: 'City', render: c => [c.city, c.state].filter(Boolean).join(', ') || '—', sortKey: 'city' },
                { key: 'source', header: 'Source', render: c => (
                  <span className="text-xs">
                    {c.legacy_ims_id ? <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 mr-1">IMS</span> : null}
                    {c.legacy_pos_id ? <span className="px-2 py-0.5 rounded bg-green-100 text-green-700">POS</span> : null}
                  </span>
                ) },
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>

      {showNew && (
        <CustomerForm
          mode="new"
          onClose={() => setShowNew(false)}
          onSaved={(c, { keepOpen }) => { if (!keepOpen) { setShowNew(false); navigate(`/customers/${c.id}`) } else { reload() } }}
        />
      )}
    </div>
  )
}
